/**
 * Overview drill-down — "who / what is behind this number?"
 *
 * Evan (2026-08-02): "I want to click on a day and a value, like July 31st, 3 new users, and see
 * who those users are… if I click 10 ratings August 1st, go see what those 10 ratings are, venue
 * name, city location, and the person that did the rating. Same for cities and top cities."
 *
 * Structure mirrors the existing modal detail views (`CityVenueDetailModal`, `UserPostsModal`,
 * `UserFriendsModal`): fixed overlay → bordered card → header row with Close → scrolling body →
 * loading / error / empty in that order. Rows that name a venue or a user open the EXISTING deeper
 * modal for it, so the drill-down keeps going.
 *
 * ── Timezone (the thing most likely to make this quietly wrong) ───────────────────────────────
 * The Home charts bucket days at midnight US Central (`DAY_TZ`), not UTC — `HomeScreen` imports
 * `dayKeyInTz` from THIS file so the bucket key and the SQL range are computed by the same code.
 * `tzDayRangeUtc('2026-07-31')` yields the half-open UTC range
 * [2026-07-31T05:00:00Z, 2026-08-01T05:00:00Z) during CDT (06:00Z during CST), which is exactly
 * the set of rows the bar counted.
 *
 * Reads only. Every column below was verified against PRODUCTION (2026-08-02).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { useEscToClose } from '../lib/useEscToClose';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { SkeletonRows } from '../ui/Skeleton';
import { CityVenueDetailModal, type VenueDetailSeed } from './CityVenueDetailModal';
import { UserPostsModal, type UserPostsUser } from './UserPostsModal';

// ---------------------------------------------------------------------------
// Day bucketing — single source of truth, shared with HomeScreen's charts.
// ---------------------------------------------------------------------------

/** All Overview day buckets cut at midnight US Central — Evan's clock, not UTC. */
export const DAY_TZ = 'America/Chicago';

const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: DAY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Central-time calendar day (YYYY-MM-DD) for an ISO timestamp or Date. */
export function dayKeyInTz(value: string | Date): string {
  return dayKeyFmt.format(typeof value === 'string' ? new Date(value) : value);
}

const wallClockFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DAY_TZ,
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** Milliseconds the DAY_TZ wall clock is ahead of UTC at `instant` (negative for Central). */
function tzOffsetMsAt(instant: Date): number {
  const parts = wallClockFmt.formatToParts(instant);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const wallAsUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    // hourCycle h23/h24 can render midnight as "24" — fold it back to 0.
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return wallAsUtc - instant.getTime();
}

/** The UTC instant of midnight-at-the-start-of `dayIso` in DAY_TZ. */
function startOfTzDayUtc(dayIso: string): Date {
  const [y, m, d] = dayIso.split('-').map(Number);
  const wall = Date.UTC(y, m - 1, d, 0, 0, 0);
  // Correct by the offset, then re-read the offset AT the corrected instant so a DST changeover
  // inside the day still lands on real local midnight (Central never skips midnight itself).
  let ts = wall - tzOffsetMsAt(new Date(wall));
  ts = wall - tzOffsetMsAt(new Date(ts));
  return new Date(ts);
}

/**
 * Half-open UTC range `[startIso, endIso)` covering one DAY_TZ calendar day.
 * Use with `.gte('created_at', startIso).lt('created_at', endIso)` — never `lte`, which would
 * double-count a row landing exactly on the next midnight.
 */
export function tzDayRangeUtc(dayIso: string): { startIso: string; endIso: string } {
  const [y, m, d] = dayIso.split('-').map(Number);
  // Pure UTC arithmetic on a date-only value — DST can't perturb "the next calendar date".
  const nextIso = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  return {
    startIso: startOfTzDayUtc(dayIso).toISOString(),
    endIso: startOfTzDayUtc(nextIso).toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Target
// ---------------------------------------------------------------------------

export type DaySeriesKind = 'signups' | 'ratings' | 'venues' | 'raters' | 'new-raters';

export type DayDrilldownTarget = {
  kind: DaySeriesKind;
  /** Central calendar day, YYYY-MM-DD — the bucket that was clicked. */
  dayIso: string;
  /** The chart's own x-axis label for that bucket ("Jul 31"). */
  dayLabel: string;
  /** Rows that landed on this Central day — what the modal lists. */
  dayValue: number;
  /**
   * Cumulative total at this point when the TREND view was clicked; null in Daily view.
   * The Trend point is a running total, so the rows behind it are still only the day's increment —
   * the header says both numbers rather than pretending the list is the whole total.
   */
  runningTotal: number | null;
  /** Only for kind 'new-raters': the user ids whose FIRST-ever rating landed on this day. */
  userIds?: string[];
  /** Only for kind 'venues': the exact venue ids the bar counted (see HomeScreen). */
  venueIds?: string[];
};

export type CityDrilldownTarget = {
  kind: 'city';
  cityId: string;
  cityName: string;
  /** `cities.total_shado_ratings` — the denormalized number the operator clicked. */
  ratingsColumn: number;
};

export type DrilldownTarget = DayDrilldownTarget | CityDrilldownTarget;

// ---------------------------------------------------------------------------
// Row shapes (columns verified against production)
// ---------------------------------------------------------------------------

type SignupRow = {
  id: string;
  username: string | null;
  full_name: string | null;
  city: string | null;
  university: string | null;
  created_at: string;
  user_number: number | null;
  referral_source: string | null;
  referred_by_username: string | null;
  is_private: boolean | null;
  is_admin: boolean | null;
  suspended_at: string | null;
  avatar_url: string | null;
};

const SIGNUP_SELECT =
  'id, username, full_name, city, university, created_at, user_number, referral_source, referred_by_username, is_private, is_admin, suspended_at, avatar_url';

type RatingRow = {
  id: string;
  user_id: string | null;
  venue_id: string | null;
  venue_name: string | null;
  elo_score: number | null;
  venue_score: number | null;
  notes: string | null;
  mood_tag: string | null;
  created_at: string;
  visit_date: string | null;
  is_public: boolean | null;
  is_event: boolean | null;
  event_name: string | null;
  deleted_at: string | null;
};

const RATING_SELECT =
  'id, user_id, venue_id, venue_name, elo_score, venue_score, notes, mood_tag, created_at, visit_date, is_public, is_event, event_name, deleted_at';

type VenueLite = {
  id: string;
  venue_name_user_input: string | null;
  osm_name: string | null;
  city_id: string | null;
  locality: string | null;
  region: string | null;
  venue_shado_rating_count: number | null;
  venue_shado_unique_users: number | null;
  venue_shado_score: number | null;
  is_hidden: boolean | null;
};

const VENUE_LITE_SELECT =
  'id, venue_name_user_input, osm_name, city_id, locality, region, venue_shado_rating_count, venue_shado_unique_users, venue_shado_score, is_hidden';

type ProfileLite = { id: string; username: string | null; full_name: string | null };

/** One venue as shown in the 'venues first rated' / city lists. */
type VenueListItem = {
  id: string;
  name: string;
  cityLabel: string;
  /** Live counts off `logs` (retired_at IS NULL) — same definition the Cities page uses. */
  ratings: number;
  uniqueRaters: number;
  /** For the first-rated list: who rated it first that day, and when. */
  firstRaterLabel?: string;
  firstRatedAt?: string;
};

/** One rater as shown in the city drill-down. */
type RaterListItem = { userId: string; label: string; count: number; lastAt: string };

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function fmtInt(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? Math.round(v).toLocaleString() : '—';
}

function fmtText(v: string | null | undefined): string {
  const s = typeof v === 'string' ? v.trim() : '';
  return s || '—';
}

/** Clock time in DAY_TZ — shown so the row visibly belongs to the Central day that was clicked. */
function fmtTimeInTz(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { timeZone: DAY_TZ, hour: 'numeric', minute: '2-digit' });
}

function venueDisplayName(v: Pick<VenueLite, 'venue_name_user_input' | 'osm_name'>): string {
  return v.venue_name_user_input?.trim() || v.osm_name?.trim() || 'Unnamed venue';
}

function userLabel(p: ProfileLite | undefined, userId: string | null): string {
  if (p?.username?.trim()) return `@${p.username.trim()}`;
  if (p?.full_name?.trim()) return p.full_name.trim();
  return userId ? `${userId.slice(0, 8)}…` : 'unknown';
}

function missingColumnError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('does not exist') || m.includes('42703') || m.includes('schema cache') || m.includes('column');
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Badge({ tone, children }: { tone: 'muted' | 'amber' | 'red' | 'violet'; children: ReactNode }) {
  const cls = {
    muted: 'border-border bg-white/[0.04] text-muted',
    amber: 'border-amber-500/40 bg-amber-500/15 text-amber-200',
    red: 'border-red-500/40 bg-red-500/15 text-red-200',
    violet: 'border-violet-500/40 bg-violet-500/15 text-violet-200',
  }[tone];
  return (
    <span
      className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {children}
    </span>
  );
}

/** Inline "go deeper" link — a venue name or @username that opens the existing detail modal. */
function DeeperLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="max-w-full truncate text-left font-medium text-violet-300/95 underline-offset-2 transition-colors hover:text-violet-200 hover:underline"
    >
      {children}
    </button>
  );
}

function ListCard({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-lg border border-border bg-rowAlt px-3 py-2">
      {children}
    </li>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">{children}</h3>
  );
}

/**
 * Live rating counts for ONE venue, straight off `logs` — the same definition the Cities page
 * feeds `CityVenueDetailModal` (`retired_at IS NULL`). Seeding from the denormalized
 * `venue_shado_*` columns instead would make that modal's drift warning permanently false.
 * Rides `logs_venue_id_idx`, so it stays O(logs for this venue).
 */
export async function fetchVenueSeed(venueId: string, name: string): Promise<VenueDetailSeed> {
  try {
    const { data, error } = await supabase
      .from('logs')
      .select('user_id')
      .eq('venue_id', venueId)
      .is('retired_at', null);
    if (error) throw error;
    const rows = (data ?? []) as { user_id: string | null }[];
    const raters = new Set(rows.map((r) => r.user_id).filter((id): id is string => Boolean(id)));
    return { id: venueId, name, ratings: rows.length, uniqueRaters: raters.size };
  } catch {
    return { id: venueId, name, ratings: 0, uniqueRaters: 0 };
  }
}

// ---------------------------------------------------------------------------
// Loaded state
// ---------------------------------------------------------------------------

type LoadedRatings = {
  logs: RatingRow[];
  venueById: Record<string, VenueLite>;
  cityNameById: Record<string, string>;
  profileById: Record<string, ProfileLite>;
};

type LoadedCity = {
  venues: VenueListItem[];
  raters: RaterListItem[];
  liveRatings: number;
  /** True when the city list had to fall back to `venue_shado_unique_users >= 1` (see loadCity). */
  bounded: boolean;
  /** True when the city's log read hit CITY_LOG_LIMIT — the lists are the newest slice only. */
  truncated: boolean;
};

type Loaded =
  | { kind: 'signups'; rows: SignupRow[]; ratingsByUser: Record<string, number> }
  | ({ kind: 'ratings' } & LoadedRatings)
  | ({ kind: 'raters' } & LoadedRatings)
  | ({ kind: 'new-raters' } & LoadedRatings)
  | { kind: 'venues'; venues: VenueListItem[] }
  | ({ kind: 'city' } & LoadedCity);

const TITLES: Record<DaySeriesKind, string> = {
  signups: 'New users',
  ratings: 'Ratings',
  venues: 'Venues rated for the first time',
  raters: 'Distinct raters',
  'new-raters': 'First-time raters',
};

const MAX_CITY_VENUES = 200;
/**
 * Newest-first cap on the city log read. The busiest city today is New Orleans at 181 logs, so this
 * is ~10x headroom; it exists so the query can never become unbounded as ratings grow, and the UI
 * says so when it bites.
 */
const CITY_LOG_LIMIT = 2000;

export function OverviewDrilldownModal({
  target,
  onClose,
}: {
  target: DrilldownTarget | null;
  onClose: () => void;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailVenue, setDetailVenue] = useState<VenueDetailSeed | null>(null);
  const [detailVenueCity, setDetailVenueCity] = useState<string | null>(null);
  const [postsUser, setPostsUser] = useState<UserPostsUser | null>(null);

  // Escape closes the TOP-most layer only — otherwise one keypress would dismiss the
  // drill-down and the venue/user modal opened from inside it at the same time.
  useEscToClose(Boolean(target) && !detailVenue && !postsUser, onClose);

  /** Resolve usernames for a set of user ids in one request (`logs.user_id` → auth.users, no FK to profiles). */
  const fetchProfiles = useCallback(async (ids: string[]): Promise<Record<string, ProfileLite>> => {
    if (ids.length === 0) return {};
    const { data } = await supabase.from('profiles').select('id, username, full_name').in('id', ids);
    const map: Record<string, ProfileLite> = {};
    for (const p of (data ?? []) as ProfileLite[]) map[p.id] = p;
    return map;
  }, []);

  const fetchCityNames = useCallback(async (ids: string[]): Promise<Record<string, string>> => {
    if (ids.length === 0) return {};
    const { data } = await supabase.from('cities').select('id, name').in('id', ids);
    const map: Record<string, string> = {};
    for (const c of (data ?? []) as { id: string; name: string | null }[]) {
      if (c.name) map[c.id] = c.name;
    }
    return map;
  }, []);

  /** Central day → the exact half-open UTC window the chart bar counted. */
  const loadSignups = useCallback(
    async (dayIso: string): Promise<Loaded> => {
      const { startIso, endIso } = tzDayRangeUtc(dayIso);
      const { data, error: e } = await supabase
        .from('profiles')
        .select(SIGNUP_SELECT)
        .gte('created_at', startIso)
        .lt('created_at', endIso)
        .order('created_at', { ascending: true });
      if (e) throw e;
      const rows = (data ?? []) as SignupRow[];

      // "Did they actually do anything?" — one bounded read on (user_id, created_at) indexes.
      const ratingsByUser: Record<string, number> = {};
      const ids = rows.map((r) => r.id);
      if (ids.length > 0) {
        const { data: logRows } = await supabase
          .from('logs')
          .select('user_id')
          .in('user_id', ids)
          .is('retired_at', null);
        for (const l of (logRows ?? []) as { user_id: string | null }[]) {
          if (l.user_id) ratingsByUser[l.user_id] = (ratingsByUser[l.user_id] ?? 0) + 1;
        }
      }
      return { kind: 'signups', rows, ratingsByUser };
    },
    [],
  );

  const loadRatings = useCallback(
    async (dayIso: string): Promise<Loaded> => {
      const { startIso, endIso } = tzDayRangeUtc(dayIso);
      // `retired_at IS NULL` mirrors the chart exactly — an edited log retires the old row and
      // inserts a new one, so counting retired rows would double-count the same visit.
      const { data, error: e } = await supabase
        .from('logs')
        .select(RATING_SELECT)
        .is('retired_at', null)
        .gte('created_at', startIso)
        .lt('created_at', endIso)
        .order('created_at', { ascending: true });
      if (e) throw e;
      const logs = (data ?? []) as RatingRow[];

      const venueIds = Array.from(
        new Set(logs.map((l) => l.venue_id).filter((id): id is string => Boolean(id))),
      );
      const userIds = Array.from(
        new Set(logs.map((l) => l.user_id).filter((id): id is string => Boolean(id))),
      );

      const [venueRes, profileById] = await Promise.all([
        venueIds.length > 0
          ? supabase.from('venues').select(VENUE_LITE_SELECT).in('id', venueIds)
          : Promise.resolve({ data: [], error: null }),
        fetchProfiles(userIds),
      ]);

      const venues = ((venueRes.data ?? []) as VenueLite[]) || [];
      const venueById: Record<string, VenueLite> = {};
      for (const v of venues) venueById[v.id] = v;

      const cityIds = Array.from(
        new Set(venues.map((v) => v.city_id).filter((id): id is string => Boolean(id))),
      );
      const cityNameById = await fetchCityNames(cityIds);

      return { kind: 'ratings', logs, venueById, cityNameById, profileById };
    },
    [fetchProfiles, fetchCityNames],
  );

  /**
   * The venue ids come from HomeScreen, which already computed each venue's FIRST rating day
   * across ALL non-retired logs. Recomputing "first" here from the day's logs alone would be
   * wrong (a venue rated again today is not new), and re-deriving it would disagree with the bar
   * the operator clicked. Passing the ids down makes the list and the bar the same set by
   * construction.
   */
  const loadFirstRatedVenues = useCallback(
    async (dayIso: string, venueIds: string[]): Promise<Loaded> => {
      if (venueIds.length === 0) return { kind: 'venues', venues: [] };
      const { startIso, endIso } = tzDayRangeUtc(dayIso);

      const [venueRes, dayLogRes, allLogRes] = await Promise.all([
        supabase.from('venues').select(VENUE_LITE_SELECT).in('id', venueIds),
        supabase
          .from('logs')
          .select('id, user_id, venue_id, created_at')
          .in('venue_id', venueIds)
          .is('retired_at', null)
          .gte('created_at', startIso)
          .lt('created_at', endIso)
          .order('created_at', { ascending: true }),
        supabase.from('logs').select('user_id, venue_id').in('venue_id', venueIds).is('retired_at', null),
      ]);
      if (venueRes.error) throw venueRes.error;
      if (dayLogRes.error) throw dayLogRes.error;

      const venues = (venueRes.data ?? []) as VenueLite[];
      const dayLogs = (dayLogRes.data ?? []) as {
        id: string;
        user_id: string | null;
        venue_id: string | null;
        created_at: string;
      }[];
      const allLogs = (allLogRes.data ?? []) as { user_id: string | null; venue_id: string | null }[];

      const userIds = Array.from(
        new Set(dayLogs.map((l) => l.user_id).filter((id): id is string => Boolean(id))),
      );
      const cityIds = Array.from(
        new Set(venues.map((v) => v.city_id).filter((id): id is string => Boolean(id))),
      );
      const [profileById, cityNameById] = await Promise.all([
        fetchProfiles(userIds),
        fetchCityNames(cityIds),
      ]);

      // Live totals per venue (all time), same definition as the Cities page.
      const totals: Record<string, { ratings: number; raters: Set<string> }> = {};
      for (const l of allLogs) {
        if (!l.venue_id) continue;
        const agg = totals[l.venue_id] ?? (totals[l.venue_id] = { ratings: 0, raters: new Set<string>() });
        agg.ratings += 1;
        if (l.user_id) agg.raters.add(l.user_id);
      }

      const firstOfDay: Record<string, { user_id: string | null; created_at: string }> = {};
      for (const l of dayLogs) {
        if (!l.venue_id || firstOfDay[l.venue_id]) continue; // ascending order → first wins
        firstOfDay[l.venue_id] = { user_id: l.user_id, created_at: l.created_at };
      }

      const venueById: Record<string, VenueLite> = {};
      for (const v of venues) venueById[v.id] = v;

      const items: VenueListItem[] = venueIds.map((id) => {
        const v = venueById[id];
        const agg = totals[id];
        const first = firstOfDay[id];
        const cityLabel = v
          ? (v.city_id ? cityNameById[v.city_id] : null) ?? fmtText(v.locality)
          : '—';
        return {
          id,
          name: v ? venueDisplayName(v) : `${id.slice(0, 8)}…`,
          cityLabel,
          ratings: agg?.ratings ?? 0,
          uniqueRaters: agg?.raters.size ?? 0,
          firstRaterLabel: first ? userLabel(profileById[first.user_id ?? ''], first.user_id) : undefined,
          firstRatedAt: first?.created_at,
        };
      });
      items.sort((a, b) => (a.firstRatedAt ?? '').localeCompare(b.firstRatedAt ?? ''));
      return { kind: 'venues', venues: items };
    },
    [fetchProfiles, fetchCityNames],
  );

  /**
   * City drill-down. Primary path filters `logs` through the `logs.venue_id → venues` FK with
   * `venues!inner(city_id)` so the count is EXACT — it matches the live count behind
   * `cities.total_shado_ratings` even when that denormalized column has drifted. It rides
   * `idx_venues_city_id`, and an id-list approach is not an option here: New York City alone has
   * 13,128 venue rows, far past a usable PostgREST `in()` URL.
   *
   * Fallback (embed unavailable): rated venues in the city (`venue_shado_unique_users >= 1`,
   * ≤ MAX_CITY_VENUES) → logs by those ids. Bounded and safe, but it can miss a venue whose
   * denormalized counter drifted to 0 — flagged in the UI as a partial list.
   */
  const loadCity = useCallback(
    async (cityId: string): Promise<Loaded> => {
      type EmbeddedLog = {
        id: string;
        user_id: string | null;
        venue_id: string | null;
        created_at: string;
        venues:
          | {
              id: string;
              venue_name_user_input: string | null;
              osm_name: string | null;
              locality: string | null;
            }
          | null;
      };

      let logs: EmbeddedLog[] | null = null;
      let bounded = false;

      const embed = await supabase
        .from('logs')
        .select('id, user_id, venue_id, created_at, venues!inner(id, venue_name_user_input, osm_name, locality)')
        .eq('venues.city_id', cityId)
        .is('retired_at', null)
        .order('created_at', { ascending: false })
        .limit(CITY_LOG_LIMIT);

      if (!embed.error) {
        logs = (embed.data ?? []) as unknown as EmbeddedLog[];
      } else if (!missingColumnError(embed.error.message) && !embed.error.message.toLowerCase().includes('relationship')) {
        throw embed.error;
      }

      if (logs == null) {
        bounded = true;
        const { data: vRows, error: vErr } = await supabase
          .from('venues')
          .select('id, venue_name_user_input, osm_name, locality')
          .eq('city_id', cityId)
          .gte('venue_shado_unique_users', 1)
          .order('venue_shado_rating_count', { ascending: false, nullsFirst: false })
          .limit(MAX_CITY_VENUES);
        if (vErr) throw vErr;
        const vLite = (vRows ?? []) as {
          id: string;
          venue_name_user_input: string | null;
          osm_name: string | null;
          locality: string | null;
        }[];
        const byId: Record<string, (typeof vLite)[number]> = {};
        for (const v of vLite) byId[v.id] = v;
        const ids = vLite.map((v) => v.id);
        if (ids.length === 0) {
          logs = [];
        } else {
          const { data: lRows, error: lErr } = await supabase
            .from('logs')
            .select('id, user_id, venue_id, created_at')
            .in('venue_id', ids)
            .is('retired_at', null)
            .order('created_at', { ascending: false })
            .limit(CITY_LOG_LIMIT);
          if (lErr) throw lErr;
          logs = ((lRows ?? []) as Omit<EmbeddedLog, 'venues'>[]).map((l) => ({
            ...l,
            venues: l.venue_id ? byId[l.venue_id] ?? null : null,
          }));
        }
      }

      const perVenue: Record<
        string,
        { name: string; locality: string | null; ratings: number; raters: Set<string> }
      > = {};
      const perRater: Record<string, { count: number; lastAt: string }> = {};
      for (const l of logs) {
        if (l.venue_id) {
          const v = l.venues;
          const agg =
            perVenue[l.venue_id] ??
            (perVenue[l.venue_id] = {
              name: v ? venueDisplayName(v) : `${l.venue_id.slice(0, 8)}…`,
              locality: v?.locality ?? null,
              ratings: 0,
              raters: new Set<string>(),
            });
          agg.ratings += 1;
          if (l.user_id) agg.raters.add(l.user_id);
        }
        if (l.user_id) {
          const r = perRater[l.user_id] ?? (perRater[l.user_id] = { count: 0, lastAt: l.created_at });
          r.count += 1;
          if (l.created_at > r.lastAt) r.lastAt = l.created_at;
        }
      }

      const raterIds = Object.keys(perRater);
      const profileById = await fetchProfiles(raterIds);

      const venues: VenueListItem[] = Object.keys(perVenue)
        .map((id) => ({
          id,
          name: perVenue[id].name,
          cityLabel: fmtText(perVenue[id].locality),
          ratings: perVenue[id].ratings,
          uniqueRaters: perVenue[id].raters.size,
        }))
        .sort((a, b) => b.ratings - a.ratings || a.name.localeCompare(b.name));

      const raters: RaterListItem[] = raterIds
        .map((id) => ({
          userId: id,
          label: userLabel(profileById[id], id),
          count: perRater[id].count,
          lastAt: perRater[id].lastAt,
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

      return {
        kind: 'city',
        venues,
        raters,
        liveRatings: logs.length,
        bounded,
        truncated: logs.length >= CITY_LOG_LIMIT,
      };
    },
    [fetchProfiles],
  );

  const load = useCallback(
    async (t: DrilldownTarget) => {
      setLoading(true);
      setError(null);
      try {
        let next: Loaded;
        if (t.kind === 'city') next = await loadCity(t.cityId);
        else if (t.kind === 'signups') next = await loadSignups(t.dayIso);
        else if (t.kind === 'ratings') next = await loadRatings(t.dayIso);
        else if (t.kind === 'raters' || t.kind === 'new-raters') {
          /* Same rows the Ratings drill fetches, re-tagged so the body groups them BY PERSON —
             and for 'new-raters', filtered to the users whose first-ever rating was this day
             (the id list travels on the target, mirroring the 'venues' pattern). */
          const r = (await loadRatings(t.dayIso)) as { kind: 'ratings' } & LoadedRatings;
          const allow = t.kind === 'new-raters' ? new Set(t.userIds ?? []) : null;
          next = {
            ...r,
            kind: t.kind,
            logs: allow ? r.logs.filter((l) => l.user_id != null && allow.has(l.user_id)) : r.logs,
          };
        }
        else next = await loadFirstRatedVenues(t.dayIso, t.venueIds ?? []);
        setLoaded(next);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load details');
        setLoaded(null);
      } finally {
        setLoading(false);
      }
    },
    [loadCity, loadSignups, loadRatings, loadFirstRatedVenues],
  );

  const targetKey =
    target == null
      ? null
      : target.kind === 'city'
        ? `city:${target.cityId}`
        : `${target.kind}:${target.dayIso}`;

  useEffect(() => {
    if (target) void load(target);
    // `targetKey` collapses the object identity to the values that actually change the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  const openVenue = useCallback(async (venueId: string, name: string, cityLabel: string | null) => {
    setDetailVenueCity(cityLabel);
    setDetailVenue(await fetchVenueSeed(venueId, name));
  }, []);

  /** `UserPostsModal` wants a full profile seed, so fetch the exact shape it types. */
  const openUser = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('id, username, full_name, created_at, is_admin, avatar_url')
      .eq('id', userId)
      .maybeSingle();
    const row = data as UserPostsUser | null;
    setPostsUser(
      row ?? { id: userId, username: null, full_name: null, created_at: new Date().toISOString(), is_admin: false },
    );
  }, []);

  const header = useMemo(() => {
    if (!target) return { title: '', subtitle: '' };
    if (target.kind === 'city') {
      return {
        title: target.cityName,
        subtitle: `${fmtInt(target.ratingsColumn)} rating${target.ratingsColumn === 1 ? '' : 's'} · cities.total_shado_ratings`,
      };
    }
    const noun =
      target.kind === 'signups'
        ? `new user${target.dayValue === 1 ? '' : 's'}`
        : target.kind === 'ratings'
          ? `rating${target.dayValue === 1 ? '' : 's'}`
          : target.kind === 'raters'
            ? `distinct rater${target.dayValue === 1 ? '' : 's'}`
            : target.kind === 'new-raters'
              ? `first-time rater${target.dayValue === 1 ? '' : 's'}`
              : `venue${target.dayValue === 1 ? '' : 's'} rated for the first time`;
    const base = `${fmtInt(target.dayValue)} ${noun} · ${target.dayIso} (midnight–midnight ${DAY_TZ})`;
    return {
      title: `${TITLES[target.kind]} · ${target.dayLabel}`,
      subtitle:
        target.runningTotal == null ? base : `${base} · running total ${fmtInt(target.runningTotal)}`,
    };
  }, [target]);

  if (!target) return null;

  const body = (() => {
    if (loading) return <SkeletonRows rows={6} />;
    if (error) return <ErrorBanner>{error}</ErrorBanner>;
    if (!loaded) return null;

    if (loaded.kind === 'signups') {
      if (loaded.rows.length === 0) {
        return (
          <EmptyState
            title="No signups on this day"
            hint={`Nobody created an account between midnight and midnight ${DAY_TZ} on ${target.kind !== 'city' ? target.dayIso : ''}.`}
          />
        );
      }
      return (
        <ul className="space-y-1.5">
          {loaded.rows.map((u) => (
            <ListCard key={u.id}>
              <div className="flex min-w-0 items-start gap-3">
                {u.avatar_url ? (
                  <img
                    src={u.avatar_url}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="mt-0.5 h-8 w-8 shrink-0 rounded-full border border-border object-cover"
                  />
                ) : (
                  <span className="mt-0.5 h-8 w-8 shrink-0 rounded-full border border-border bg-surfaceElevated" />
                )}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <DeeperLink onClick={() => void openUser(u.id)}>
                      {u.username ? `@${u.username}` : `${u.id.slice(0, 8)}…`}
                    </DeeperLink>
                    {u.is_admin ? <Badge tone="violet">Admin</Badge> : null}
                    {u.is_private ? <Badge tone="muted">Private</Badge> : null}
                    {u.suspended_at ? <Badge tone="red">Suspended</Badge> : null}
                  </div>
                  <div className="truncate text-[11px] text-muted">{fmtText(u.full_name)}</div>
                  <div className="truncate text-[11px] text-mutedDim">
                    {fmtText(u.city)}
                    {u.university?.trim() ? ` · ${u.university.trim()}` : ''}
                    {u.referral_source?.trim() ? ` · via ${u.referral_source.trim()}` : ''}
                    {u.referred_by_username?.trim() ? ` · ref @${u.referred_by_username.trim()}` : ''}
                  </div>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[11px] tabular-nums text-muted">
                  {fmtInt(loaded.ratingsByUser[u.id] ?? 0)} rating
                  {(loaded.ratingsByUser[u.id] ?? 0) === 1 ? '' : 's'}
                </div>
                <div className="text-[11px] tabular-nums text-mutedDim">{fmtTimeInTz(u.created_at)}</div>
                <div className="text-[10px] tabular-nums text-mutedDim">
                  {u.user_number != null ? `#${u.user_number}` : timeAgo(u.created_at)}
                </div>
              </div>
            </ListCard>
          ))}
        </ul>
      );
    }

    if (loaded.kind === 'raters' || loaded.kind === 'new-raters') {
      if (loaded.logs.length === 0) {
        return loaded.kind === 'raters' ? (
          <EmptyState title="Nobody rated on this day" hint="No non-retired logs landed in this window." />
        ) : (
          <EmptyState
            title="No first-time raters on this day"
            hint="Everyone who rated this day had rated before — this bar counts a person only on the day of their FIRST ever rating."
          />
        );
      }
      const byUser = new Map<string, typeof loaded.logs>();
      for (const l of loaded.logs) {
        const k = l.user_id ?? 'unknown';
        const arr = byUser.get(k);
        if (arr) arr.push(l);
        else byUser.set(k, [l]);
      }
      /* Dense on purpose (Evan 2026-09-08): the unit of scanning is the PERSON — one hairline row
         per user with their ratings as micro-rows beneath, so a day with thirty raters who each
         logged a crawl still scrolls in one motion. */
      return (
        <div>
          {Array.from(byUser.entries()).map(([uid, rows]) => (
            <div key={uid} className="border-b border-white/5 py-1.5 last:border-0">
              <div className="flex items-center gap-2">
                {uid !== 'unknown' ? (
                  <DeeperLink onClick={() => void openUser(uid)}>
                    {userLabel(loaded.profileById[uid], uid)}
                  </DeeperLink>
                ) : (
                  <span className="text-[12px] text-mutedDim">unknown rater</span>
                )}
                {loaded.kind === 'new-raters' ? <Badge tone="violet">First ever</Badge> : null}
                <span className="ml-auto shrink-0 text-[10px] tabular-nums text-mutedDim">
                  {rows.length} rating{rows.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="mt-0.5">
                {rows.map((l) => {
                  const v = l.venue_id ? loaded.venueById[l.venue_id] : undefined;
                  const name = v ? venueDisplayName(v) : l.venue_name?.trim() || l.event_name?.trim() || '—';
                  const city = v?.city_id ? loaded.cityNameById[v.city_id] ?? null : null;
                  return (
                    <div key={l.id} className="flex items-center gap-2 pl-3 text-[10.5px] leading-[15px]">
                      {l.venue_id ? (
                        <button
                          type="button"
                          onClick={() => void openVenue(l.venue_id as string, name, city)}
                          className="min-w-0 truncate text-left text-muted hover:text-white"
                        >
                          {name}
                        </button>
                      ) : (
                        <span className="min-w-0 truncate text-muted">{name}</span>
                      )}
                      <span className="ml-auto shrink-0 tabular-nums text-mutedDim">
                        {typeof l.elo_score === 'number' ? Math.round(l.elo_score) : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (loaded.kind === 'ratings') {
      if (loaded.logs.length === 0) {
        return <EmptyState title="No ratings on this day" hint="No non-retired logs landed in this window." />;
      }
      return (
        <ul className="space-y-1.5">
          {loaded.logs.map((l) => {
            const v = l.venue_id ? loaded.venueById[l.venue_id] : undefined;
            const name = v ? venueDisplayName(v) : l.venue_name?.trim() || l.event_name?.trim() || '—';
            const city = v?.city_id ? loaded.cityNameById[v.city_id] ?? null : null;
            const place = [city ?? v?.locality ?? null, v?.region ?? null]
              .filter((s): s is string => Boolean(s?.trim()))
              .join(', ');
            return (
              <ListCard key={l.id}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {l.venue_id ? (
                      <DeeperLink onClick={() => void openVenue(l.venue_id as string, name, city)}>
                        {name}
                      </DeeperLink>
                    ) : (
                      <span className="truncate text-[12px] font-medium text-white">{name}</span>
                    )}
                    {l.is_event ? <Badge tone="muted">Event</Badge> : null}
                    {l.is_public === false ? <Badge tone="muted">Private</Badge> : null}
                    {l.deleted_at ? <Badge tone="red">Deleted</Badge> : null}
                    {!l.venue_id ? <Badge tone="amber">No venue</Badge> : null}
                  </div>
                  <div className="truncate text-[11px] text-mutedDim">{place || '—'}</div>
                  <div className="mt-0.5 text-[11px] text-muted">
                    {l.user_id ? (
                      <DeeperLink onClick={() => void openUser(l.user_id as string)}>
                        {userLabel(loaded.profileById[l.user_id], l.user_id)}
                      </DeeperLink>
                    ) : (
                      <span className="text-mutedDim">unknown rater</span>
                    )}
                  </div>
                  {l.notes?.trim() ? (
                    <div className="mt-0.5 truncate text-[11px] text-mutedDim">{l.notes}</div>
                  ) : null}
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[12px] tabular-nums text-white">
                    {typeof l.elo_score === 'number' ? Math.round(l.elo_score) : '—'}
                  </div>
                  <div className="text-[11px] tabular-nums text-mutedDim">{fmtTimeInTz(l.created_at)}</div>
                  {l.mood_tag?.trim() ? (
                    <div className="text-[10px] text-mutedDim">{l.mood_tag.trim()}</div>
                  ) : null}
                </div>
              </ListCard>
            );
          })}
        </ul>
      );
    }

    if (loaded.kind === 'venues') {
      if (loaded.venues.length === 0) {
        return (
          <EmptyState
            title="No venues crossed their first rating on this day"
            hint="This bar counts a venue on the day it got its FIRST non-retired rating, so it is empty whenever every rating that day went to an already-rated venue."
          />
        );
      }
      return (
        <ul className="space-y-1.5">
          {loaded.venues.map((v) => (
            <ListCard key={v.id}>
              <div className="min-w-0">
                <DeeperLink onClick={() => void openVenue(v.id, v.name, v.cityLabel)}>{v.name}</DeeperLink>
                <div className="truncate text-[11px] text-mutedDim">{v.cityLabel}</div>
                {v.firstRaterLabel ? (
                  <div className="truncate text-[11px] text-muted">
                    First rated by {v.firstRaterLabel}
                    {v.firstRatedAt ? ` at ${fmtTimeInTz(v.firstRatedAt)}` : ''}
                  </div>
                ) : null}
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[11px] tabular-nums text-muted">
                  {fmtInt(v.ratings)} rating{v.ratings === 1 ? '' : 's'}
                </div>
                <div className="text-[11px] tabular-nums text-mutedDim">
                  {fmtInt(v.uniqueRaters)} rater{v.uniqueRaters === 1 ? '' : 's'}
                </div>
              </div>
            </ListCard>
          ))}
        </ul>
      );
    }

    // city
    const drift =
      target.kind === 'city' &&
      !loaded.bounded &&
      !loaded.truncated &&
      loaded.liveRatings !== target.ratingsColumn;
    return (
      <div className="space-y-5">
        {loaded.truncated ? (
          <p className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
            Showing the newest {fmtInt(CITY_LOG_LIMIT)} ratings only — this city has more, so the per-venue
            and per-rater totals below are a partial slice.
          </p>
        ) : null}
        {drift ? (
          <p className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
            Live count of `logs` for this city is {fmtInt(loaded.liveRatings)}, but
            `cities.total_shado_ratings` says {fmtInt(target.kind === 'city' ? target.ratingsColumn : 0)} — the
            denormalized city counter has drifted.
          </p>
        ) : null}
        {loaded.bounded ? (
          <p className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
            Partial list — the join through `venues` was unavailable, so this falls back to the top{' '}
            {MAX_CITY_VENUES} venues with `venue_shado_unique_users ≥ 1`. A venue whose counter drifted to 0
            would be missing.
          </p>
        ) : null}

        <section>
          <SectionTitle>Rated venues ({fmtInt(loaded.venues.length)})</SectionTitle>
          {loaded.venues.length === 0 ? (
            <EmptyState compact title="No rated venues" hint="No non-retired logs point at a venue in this city." />
          ) : (
            <ul className="space-y-1.5">
              {loaded.venues.map((v) => (
                <ListCard key={v.id}>
                  <div className="min-w-0">
                    <DeeperLink
                      onClick={() =>
                        void openVenue(v.id, v.name, target.kind === 'city' ? target.cityName : null)
                      }
                    >
                      {v.name}
                    </DeeperLink>
                    {v.cityLabel !== '—' ? (
                      <div className="truncate text-[11px] text-mutedDim">{v.cityLabel}</div>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[11px] tabular-nums text-muted">
                      {fmtInt(v.ratings)} rating{v.ratings === 1 ? '' : 's'}
                    </div>
                    <div className="text-[11px] tabular-nums text-mutedDim">
                      {fmtInt(v.uniqueRaters)} rater{v.uniqueRaters === 1 ? '' : 's'}
                    </div>
                  </div>
                </ListCard>
              ))}
            </ul>
          )}
        </section>

        <section>
          <SectionTitle>Raters ({fmtInt(loaded.raters.length)})</SectionTitle>
          {loaded.raters.length === 0 ? (
            <EmptyState compact title="No raters" hint="Nobody has logged a venue in this city yet." />
          ) : (
            <ul className="space-y-1.5">
              {loaded.raters.map((r) => (
                <ListCard key={r.userId}>
                  <div className="min-w-0">
                    <DeeperLink onClick={() => void openUser(r.userId)}>{r.label}</DeeperLink>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[11px] tabular-nums text-muted">
                      {fmtInt(r.count)} rating{r.count === 1 ? '' : 's'}
                    </div>
                    <div className="text-[11px] tabular-nums text-mutedDim">{timeAgo(r.lastAt)}</div>
                  </div>
                </ListCard>
              ))}
            </ul>
          )}
        </section>
      </div>
    );
  })();

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
        role="dialog"
        aria-modal="true"
        aria-label={header.title}
        onClick={onClose}
      >
        <div
          className="w-full max-w-3xl rounded-xl border border-border bg-surface"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <h2 className="admin-label-primary truncate text-base">{header.title}</h2>
              <div className="mt-1 text-xs text-mutedDim">{header.subtitle}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white"
            >
              Close
            </button>
          </div>
          <div className="max-h-[75vh] overflow-y-auto px-5 py-4">{body}</div>
        </div>
      </div>

      <CityVenueDetailModal
        venue={detailVenue}
        cityName={detailVenueCity}
        onClose={() => setDetailVenue(null)}
      />
      <UserPostsModal user={postsUser} onClose={() => setPostsUser(null)} />
    </>
  );
}
